import { createElement, useCallback } from "react";

import { EmailContentType, Emptyable } from "@simplyk/common";
import { useForm, useWatch } from "react-hook-form";

import { useRecipientPlaceholder } from "../../EmailRecipientEditor/useRecipientPlaceholder";
import { EmailContent } from "../types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAiUnavailableText } from "@/common/hooks/useAiUnavailableText";
import { useTranslate } from "@/common/hooks/useTranslate";
import { EmailEditorFormData } from "@/common/types/email";
import { ArrowDown, ArrowUp, Sparks } from "@/icons/outlined";

type UseFooterActionsProps = {
  footerActions?: EmailFooterAction[];
  form: ReturnType<typeof useForm<EmailEditorFormData>>;
  handleSendEmail: () => void;
  loading?: boolean;
  emailType: Emptyable<EmailContentType>;
  emailContent: EmailContent;
  isGenerating?: boolean;
  generateEmail: (transactionId: string, length?: "shorter" | "longer" | "regenerate") => Promise<void>;
  recipientsCount: number;
  hasRecipients: boolean;
  recipientsStatus: "loading" | "ready" | "error";
};

export type EmailFooterAction = Partial<Omit<RichEmailFooterAction, "onClick">> | RichEmailFooterAction["key"];

export type EmailFooterProps = {
  actions: RichEmailFooterAction[];
  disableActions: boolean;
};

export type RichEmailFooterAction = {
  key: "send" | "generate-description" | "generate-shorter-description" | "generate-longer-description";
  label: string;
  onClick: ((data: EmailEditorFormData) => void) | (() => void);
  disabled?: boolean;
  tooltipText?: string;
  isLoading?: boolean;
  startIcon?: React.ReactNode;
};

const getFilteredActions = (
  defaultActions: RichEmailFooterAction[],
  thankYouTransactionId: string | undefined,
  footerActions?: EmailFooterAction[]
): RichEmailFooterAction[] => {
  // Editing a thank you email - show send + AI generation buttons only
  if (thankYouTransactionId) {
    return defaultActions.filter(
      (action) =>
        action.key === "send" ||
        action.key === "generate-shorter-description" ||
        action.key === "generate-longer-description" ||
        action.key === "generate-description"
    );
  }

  if (footerActions) {
    return footerActions.reduce((acc, action) => {
      if (typeof action !== "string") {
        const defaultAction = defaultActions.find((defaultAction) => defaultAction.key === action.key);
        return [...acc, { ...defaultAction, ...action } as RichEmailFooterAction];
      }

      const defaultAction = defaultActions.find((defaultAction) => defaultAction.key === action);
      if (defaultAction) {
        return [...acc, defaultAction];
      }

      return acc;
    }, [] as RichEmailFooterAction[]);
  }

  return defaultActions;
};

export const useEmailFooter = ({
  footerActions,
  form,
  handleSendEmail,
  loading,
  emailType,
  emailContent,
  isGenerating = false,
  generateEmail: generateEmailProp,
  recipientsCount,
  hasRecipients,
  recipientsStatus,
}: UseFooterActionsProps) => {
  const { t } = useTranslate();
  const { data: refineAvailability } = trpc.getAiAvailability.useQuery(
    { feature: "extra-thank-you-email" },
    { enabled: Boolean(emailContent.thankYouTransactionId) }
  );
  const refineUnavailableText = useAiUnavailableText(refineAvailability);

  const subject = useWatch({ control: form.control, name: "subject" });

  const handleGenerateEmail = useCallback(
    async (length?: "shorter" | "longer" | "regenerate") => {
      if (!emailContent.thankYouTransactionId) {
        return;
      }

      await generateEmailProp(emailContent.thankYouTransactionId, length);
    },
    [emailContent.thankYouTransactionId, generateEmailProp]
  );

  const { isSendingEmailForbidden, isStripeCustomAccountActive } = useCurrentUserContext();
  const organizationWithoutStripeAccountHasSentTooMuchEmail = recipientsCount > 500 && !isStripeCustomAccountActive;

  const recipientPlaceholder = useRecipientPlaceholder({ emailType, hasRecipients });

  const isWorkflowStepEmail = emailType === EmailContentType.CRMWORKFLOW;

  // If there is a recipient placeholder, it means the recipients will be dynamically added to the email
  const sendNowOrScheduleDisabled =
    recipientsStatus !== "ready" ||
    (!hasRecipients && !recipientPlaceholder) ||
    organizationWithoutStripeAccountHasSentTooMuchEmail ||
    !subject ||
    isGenerating;

  const defaultActions: RichEmailFooterAction[] = [
    {
      key: "send",
      onClick: handleSendEmail,
      label: isWorkflowStepEmail ? t("dashboard", "common.save") : t("dashboard", "tickets.sendNow"),
      disabled: isWorkflowStepEmail ? !subject || isGenerating : sendNowOrScheduleDisabled,
      tooltipText:
        recipientsStatus === "error" && !isWorkflowStepEmail
          ? t("common", "tryAgainOrContactUs")
          : !hasRecipients && !isWorkflowStepEmail
            ? t("dashboard", "emailDrawerAction.sendNow.tooltipRecipients")
            : !subject
              ? t("dashboard", "emailDrawerAction.sendNow.tooltipSubject")
              : undefined,
      isLoading: loading,
    },
    // Only show AI generation buttons when editing a thank you email
    ...(emailContent.thankYouTransactionId
      ? [
          {
            key: "generate-shorter-description" as const,
            onClick: () => handleGenerateEmail("shorter"),
            label: t("dashboard", "EmailDrawer.actions.generateShortDescription"),
            disabled: isGenerating,
            isLoading: isGenerating,
            startIcon: createElement(ArrowDown),
          },
          {
            key: "generate-longer-description" as const,
            onClick: () => handleGenerateEmail("longer"),
            label: t("dashboard", "EmailDrawer.actions.generateLongDescription"),
            disabled: isGenerating,
            isLoading: isGenerating,
            startIcon: createElement(ArrowUp),
          },
          {
            key: "generate-description" as const,
            onClick: () => handleGenerateEmail("regenerate"),
            label: t("dashboard", "EmailDrawer.actions.generateDescription"),
            disabled: isGenerating,
            isLoading: isGenerating,
            startIcon: createElement(Sparks),
          },
        ]
      : []),
  ];

  const actions = getFilteredActions(defaultActions, emailContent.thankYouTransactionId, footerActions);

  return {
    actions,
    disableActions: isSendingEmailForbidden && !isWorkflowStepEmail,
    generatingEmail: isGenerating,
    refineUnavailableText,
  };
};
